import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { ArrowLeft, X } from 'lucide-react';
import styles from './SearchOverlay.module.scss';

export interface SearchOverlayHandle {
  skipNextHistoryBack: () => void;
}

interface SearchOverlayProps {
  isOpen: boolean;
  onClose: () => void;
  query: string;
  onQueryChange: (query: string) => void;
  placeholder?: string;
  shortcutHint?: string;
  onKeyDown?: (e: React.KeyboardEvent) => void;
  /** When false, skips history push/pop (avoids conflict with parent history, e.g. when auto-opened in a wizard) */
  useHistory?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
}

const SearchOverlay = forwardRef<SearchOverlayHandle, SearchOverlayProps>(
  function SearchOverlay(
    {
      isOpen,
      onClose,
      query,
      onQueryChange,
      placeholder = 'Search...',
      shortcutHint,
      onKeyDown,
      useHistory = true,
      children,
      footer,
    },
    ref,
  ) {
    const inputRef = useRef<HTMLInputElement>(null);
    const closedByPopStateRef = useRef(false);

    useImperativeHandle(ref, () => ({
      skipNextHistoryBack: () => {
        closedByPopStateRef.current = true;
      },
    }));

    useEffect(() => {
      if (isOpen) {
        requestAnimationFrame(() => inputRef.current?.focus());
      }
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen || !useHistory) return;

      closedByPopStateRef.current = false;
      window.history.pushState({ searchOverlay: true }, '');

      const handlePopState = () => {
        closedByPopStateRef.current = true;
        onClose();
      };

      window.addEventListener('popstate', handlePopState);
      return () => {
        window.removeEventListener('popstate', handlePopState);
        if (!closedByPopStateRef.current) {
          window.history.back();
        }
      };
    }, [isOpen, onClose, useHistory]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }
      onKeyDown?.(e);
    };

    if (!isOpen) return null;

    return (
      <div className={styles.overlay} onMouseDown={onClose}>
        <div
          className={styles.panel}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={handleKeyDown}
          role="dialog"
          aria-label="Search"
        >
          <div className={styles.inputWrapper}>
            <button
              className={styles.backButton}
              onClick={onClose}
              aria-label="Close"
              type="button"
            >
              <ArrowLeft size={20} />
            </button>
            <input
              ref={inputRef}
              type="text"
              className={styles.input}
              placeholder={placeholder}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              aria-label="Search"
              autoComplete="off"
            />
            {query && (
              <button
                className={styles.clearButton}
                onClick={() => {
                  onQueryChange('');
                  inputRef.current?.focus();
                }}
                aria-label="Clear search"
                type="button"
              >
                <X size={16} />
              </button>
            )}
            {shortcutHint && (
              <kbd className={styles.shortcutHint}>{shortcutHint}</kbd>
            )}
          </div>
          <div className={styles.results}>{children}</div>
          {footer && <div className={styles.footer}>{footer}</div>}
        </div>
      </div>
    );
  },
);

export default SearchOverlay;
